import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  BarChart3,
  BookOpen,
  Brain,
  Eye,
  Sparkles,
  Target,
  TrendingUp,
} from "lucide-react";

import { Container } from "@/platform/layout/container";
import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";

/**
 * `/usp/banca-fuvest` — análise pública da assinatura da banca Fuvest.
 *
 * Conteúdo derivado da análise de 268 questões classificadas do corpus
 * Fuvest 2024+2025+2026 (POC Gate 3). Reflete tendências reais observadas;
 * será refinado conforme corpus expande para 10 anos.
 */

const COGNITIVE_SKILLS = [
  { label: "Leitura crítica", count: 88, percent: 33 },
  { label: "Raciocínio lógico", count: 62, percent: 23 },
  { label: "Interpretar enunciado", count: 38, percent: 14 },
  { label: "Interpretar gráfico", count: 18, percent: 7 },
  { label: "Modelar problema", count: 18, percent: 7 },
  { label: "Aplicar fórmula", count: 16, percent: 6 },
  { label: "Síntese", count: 12, percent: 4 },
  { label: "Outras", count: 16, percent: 6 },
];

const QUESTION_TYPES = [
  { label: "Contextualizada", count: 100, hint: "Problema real (social, ambiental, econômico)" },
  { label: "Interpretação pura", count: 50, hint: "Sem cálculo; baseado em texto" },
  { label: "Gráfica", count: 29, hint: "Resposta depende de leitura gráfica" },
  { label: "Literária", count: 28, hint: "Análise de obra/excerto obrigatório" },
  { label: "Historiográfica", count: 27, hint: "Comparação entre fontes/períodos" },
  { label: "Cálculo direto", count: 15, hint: "Aplicação imediata de fórmula" },
];

const ERROR_RISKS = [
  { label: "Distrator plausível", count: 144, percent: 54, description: "Alternativa errada parece muito boa — exige descarte ativo." },
  { label: "Armadilha conceitual", count: 102, percent: 38, description: "Confusão entre conceitos próximos — sabe o conteúdo mas confunde sob pressão." },
  { label: "Unidades / conversão", count: 11, percent: 4, description: "Conta correta com unidade errada — perde ponto fácil." },
  { label: "Pegadinha numérica", count: 8, percent: 3, description: "Resultado-armadilha plausível entre as alternativas." },
];

const DISCIPLINE_WEIGHTS = [
  { name: "Geografia", count: 34, pct: 12.6 },
  { name: "Física", count: 31, pct: 11.5 },
  { name: "Matemática", count: 29, pct: 10.7 },
  { name: "Química", count: 28, pct: 10.4 },
  { name: "História", count: 26, pct: 9.6 },
  { name: "Biologia", count: 24, pct: 8.9 },
  { name: "Literatura", count: 23, pct: 8.5 },
  { name: "Inglês", count: 19, pct: 7.0 },
  { name: "Sociologia", count: 16, pct: 5.9 },
  { name: "Interdisciplinar", count: 13, pct: 4.8 },
  { name: "Português", count: 13, pct: 4.8 },
  { name: "Filosofia", count: 8, pct: 3.0 },
  { name: "Artes", count: 6, pct: 2.2 },
];

const TAKEAWAYS: Array<{ icon: typeof Brain; title: string; body: string }> = [
  {
    icon: Eye,
    title: "Leitura crítica é o pilar.",
    body: "33% das questões exigem leitura crítica como habilidade principal — distinguir tese de argumento, identificar viés, separar fato de inferência. Treinar resolução de questões objetivas de Fuvest sem trabalhar leitura crítica é otimizar a coisa errada.",
  },
  {
    icon: Target,
    title: "Contextualização é o gênero dominante.",
    body: "37% das questões são contextualizadas — problema real com camada de leitura antes do conteúdo técnico. Banca não pede cálculo seco; pede modelar contexto em ferramenta matemática/física/química.",
  },
  {
    icon: Brain,
    title: "Quase toda questão tem distrator plausível.",
    body: "54% têm distrator plausível e 38% têm armadilha conceitual — 92% das questões exigem descarte ativo, não escolha direta. A estratégia 'leio e marco a primeira correta' falha 9 em 10 vezes.",
  },
  {
    icon: BarChart3,
    title: "Peso real distribuído equilibradamente.",
    body: "Geografia (12,6%), Física (11,5%), Matemática (10,7%), Química (10,4%) — top 4 disciplinas somam 45% da prova. Nenhuma matéria domina absoluto; cobertura ampla é necessária.",
  },
];

export function UspBancaFuvestPage() {
  return (
    <div className="relative overflow-hidden">
      {/* Hero */}
      <section className="relative overflow-hidden bg-mesh">
        <Blob color="sky" className="pointer-events-none absolute -left-32 -top-20 w-96 opacity-25 blur-2xl animate-blob" />
        <SoftStar color="mint" className="pointer-events-none absolute right-12 bottom-20 w-20 opacity-50 animate-float" />

        <Container size="wide" className="relative pt-16 pb-20 md:pt-24">
          <Link
            to="/usp"
            className="inline-flex items-center gap-1.5 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] transition-colors hover:text-[color:var(--color-foreground)]"
          >
            <ArrowLeft className="size-3.5" aria-hidden />
            Voltar
          </Link>

          <div className="mt-8 max-w-3xl animate-fade-up">
            <Badge
              className="gap-1.5"
              style={{ backgroundColor: "var(--color-accent)", color: "white", border: "none" }}
            >
              <Sparkles className="size-3" aria-hidden />
              Banca Fuvest · análise do corpus
            </Badge>

            <h1 className="mt-7 font-display text-[length:var(--text-display-xl)] font-semibold leading-[var(--text-display-xl--line-height)] tracking-[var(--text-display-xl--letter-spacing)] text-balance">
              A assinatura da{" "}
              <span className="text-gradient-accent">banca Fuvest</span> em dados.
            </h1>

            <p className="mt-6 max-w-2xl text-[length:var(--text-body-lg)] text-[color:var(--color-muted-foreground)] leading-[var(--text-body-lg--line-height)] text-pretty">
              Análise de <strong>268 questões objetivas</strong> da Fuvest (2024, 2025, 2026)
              classificadas em taxonomia de 9 níveis. Padrões observados — não opiniões — mostram
              exatamente o que a banca premia e penaliza.
            </p>

            <p className="mt-3 text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
              Fonte: acervo público fuvest.br · taxonomia FuvestPro USP · atualizado 2026-05-28
            </p>
          </div>
        </Container>
      </section>

      {/* 4 Takeaways principais */}
      <section className="border-t border-[--color-border] py-20 md:py-24">
        <Container size="wide">
          <div className="max-w-2xl">
            <h2 className="font-display text-[length:var(--text-display-md)] font-semibold leading-tight tracking-[var(--text-display-md--letter-spacing)] text-balance">
              4 verdades sobre a Fuvest.
            </h2>
            <p className="mt-3 text-[length:var(--text-body-lg)] text-[color:var(--color-muted-foreground)]">
              Cada uma derivada da análise estatística — não folclore de cursinho.
            </p>
          </div>

          <ul className="mt-12 grid gap-5 sm:grid-cols-2">
            {TAKEAWAYS.map(({ icon: Icon, title, body }, idx) => (
              <li
                key={title}
                className="rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-7 shadow-[var(--shadow-sm)] lift"
              >
                <div className="flex items-center justify-between">
                  <span
                    className="chip size-10"
                    style={{ backgroundColor: "var(--color-accent-soft)", color: "var(--color-accent-deep)" }}
                  >
                    <Icon className="size-5" aria-hidden />
                  </span>
                  <span
                    data-tabular="true"
                    className="font-mono text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]"
                  >
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="mt-5 font-display text-[length:var(--text-body-lg)] font-semibold leading-tight">
                  {title}
                </h3>
                <p className="mt-3 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)] leading-relaxed">
                  {body}
                </p>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      {/* Distribuição por disciplina */}
      <section className="border-t border-[--color-border] py-20 md:py-24">
        <Container size="wide">
          <div className="grid gap-10 lg:grid-cols-[1fr_1.4fr] lg:items-start">
            <div>
              <Badge variant="outline" className="w-fit">Peso por disciplina</Badge>
              <h2 className="mt-5 font-display text-[length:var(--text-display-md)] font-semibold leading-tight tracking-[var(--text-display-md--letter-spacing)] text-balance">
                Nenhuma matéria domina.
              </h2>
              <p className="mt-4 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)] leading-relaxed">
                Top 4 (Geografia + Física + Matemática + Química) somam 45% da prova. Bottom 4 (Artes
                + Filosofia + Português + Interdisciplinar) somam 14%. Cobertura completa é
                necessária — não há atalho.
              </p>
              <p className="mt-3 text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
                Para Eng. Produção (Poli), pesos da 2ª fase mudam: Mat × 4, Fís × 3, Quím × 2.
              </p>
            </div>
            <dl className="space-y-2.5">
              {DISCIPLINE_WEIGHTS.map((d) => (
                <div key={d.name} className="grid grid-cols-[1fr_auto] items-center gap-3">
                  <div className="flex items-center gap-3">
                    <span className="w-28 text-[length:var(--text-body-sm)] font-medium">
                      {d.name}
                    </span>
                    <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-[color:var(--color-muted)]">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${(d.pct / 13) * 100}%`,
                          backgroundColor: "var(--color-accent)",
                        }}
                      />
                    </div>
                  </div>
                  <span
                    data-tabular="true"
                    className="text-[length:var(--text-body-sm)] font-medium tabular-nums text-[color:var(--color-muted-foreground)]"
                  >
                    {d.count} ({d.pct}%)
                  </span>
                </div>
              ))}
            </dl>
          </div>
        </Container>
      </section>

      {/* Habilidades cognitivas */}
      <section className="border-t border-[--color-border] py-20 md:py-24">
        <Container size="wide">
          <div className="max-w-2xl">
            <Badge variant="outline" className="w-fit">Habilidades cognitivas exigidas</Badge>
            <h2 className="mt-5 font-display text-[length:var(--text-display-md)] font-semibold leading-tight tracking-[var(--text-display-md--letter-spacing)] text-balance">
              Cabeça antes de fórmula.
            </h2>
            <p className="mt-4 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)] leading-relaxed">
              60% do esforço cognitivo está em <strong>leitura crítica</strong> +{" "}
              <strong>raciocínio lógico</strong> + <strong>interpretar enunciado</strong>. Apenas 6%
              do total exige aplicação direta de fórmula. Treinar fórmula sem treinar leitura
              produz nota abaixo de corte.
            </p>
          </div>

          <ul className="mt-12 grid gap-3 sm:grid-cols-2">
            {COGNITIVE_SKILLS.map((s) => (
              <li
                key={s.label}
                className="flex items-center justify-between gap-4 rounded-xl border border-[--color-border] bg-[color:var(--color-card)] px-5 py-3.5"
              >
                <span className="text-[length:var(--text-body-sm)] font-medium">{s.label}</span>
                <div className="flex items-center gap-3">
                  <span
                    data-tabular="true"
                    className="font-display text-[length:var(--text-body)] font-semibold tabular-nums"
                    style={{ color: "var(--color-accent-deep)" }}
                  >
                    {s.percent}%
                  </span>
                  <span className="text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)] tabular-nums">
                    ({s.count})
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      {/* Tipos de questão */}
      <section className="border-t border-[--color-border] py-20 md:py-24 bg-[color:var(--color-muted)]/30">
        <Container size="wide">
          <div className="max-w-2xl">
            <Badge variant="outline" className="w-fit">Tipos de questão</Badge>
            <h2 className="mt-5 font-display text-[length:var(--text-display-md)] font-semibold leading-tight tracking-[var(--text-display-md--letter-spacing)] text-balance">
              Contextualizada domina (37%).
            </h2>
            <p className="mt-4 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)] leading-relaxed">
              A Fuvest tipicamente embrulha conteúdo técnico em camada de leitura — problema real,
              fonte primária, gráfico, mapa. Quase nunca pede "calcule X usando a fórmula Y".
            </p>
          </div>

          <ul className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {QUESTION_TYPES.map((t) => (
              <li
                key={t.label}
                className="rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-6 shadow-[var(--shadow-sm)]"
              >
                <div className="flex items-baseline justify-between">
                  <h3 className="font-display text-[length:var(--text-body-lg)] font-semibold">
                    {t.label}
                  </h3>
                  <span
                    data-tabular="true"
                    className="font-display text-[length:var(--text-display-sm)] font-semibold tabular-nums"
                    style={{ color: "var(--color-accent-deep)" }}
                  >
                    {t.count}
                  </span>
                </div>
                <p className="mt-2 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] leading-relaxed">
                  {t.hint}
                </p>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      {/* Riscos de erro */}
      <section className="border-t border-[--color-border] py-20 md:py-24">
        <Container size="wide">
          <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr] lg:items-start">
            <div>
              <Badge variant="outline" className="w-fit">Onde a estudante perde ponto</Badge>
              <h2 className="mt-5 font-display text-[length:var(--text-display-md)] font-semibold leading-tight tracking-[var(--text-display-md--letter-spacing)] text-balance">
                92% das questões têm armadilha desenhada.
              </h2>
              <p className="mt-4 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)] leading-relaxed">
                Apenas 8% das questões são "limpas". A maioria tem distrator plausível ou armadilha
                conceitual — pegadinhas que a banca implanta intencionalmente para separar quem
                <em> sabe</em> de quem <em>está chutando bem</em>.
              </p>
              <p className="mt-3 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] leading-relaxed">
                <strong>Implicação:</strong> estratégia de "leio e marco a primeira que parece
                correta" perde ponto sistematicamente. Eliminação ativa + releitura do enunciado é
                obrigatório.
              </p>
            </div>
            <ul className="space-y-4">
              {ERROR_RISKS.map((r) => (
                <li
                  key={r.label}
                  className="rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-6 shadow-[var(--shadow-sm)]"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-[length:var(--text-body-lg)] font-semibold">
                      {r.label}
                    </h3>
                    <span
                      data-tabular="true"
                      className="font-display text-[length:var(--text-display-sm)] font-semibold tabular-nums"
                      style={{ color: "var(--color-danger)" }}
                    >
                      {r.percent}%
                    </span>
                  </div>
                  <p className="mt-2 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] leading-relaxed">
                    {r.description}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </Container>
      </section>

      {/* CTA */}
      <section className="border-t border-[--color-border] py-20 md:py-24">
        <Container size="default">
          <div className="rounded-3xl bg-mesh p-10 text-center md:p-16">
            <Badge
              className="gap-1.5"
              style={{ backgroundColor: "var(--color-accent)", color: "white", border: "none" }}
            >
              <TrendingUp className="size-3" aria-hidden />
              FuvestPro USP
            </Badge>
            <h2 className="mt-5 font-display text-[length:var(--text-display-md)] font-semibold leading-tight tracking-[var(--text-display-md--letter-spacing)] text-balance">
              Treinar com esses padrões em mente.
            </h2>
            <p className="mt-4 mx-auto max-w-xl text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)] leading-relaxed">
              FuvestPro USP usa estes mesmos dados estatísticos para calibrar simulados, focar plano
              adaptativo nas habilidades de maior peso e marcar onde a estudante cai em distratores.
            </p>
            <div className="mt-8 flex flex-col items-stretch gap-3 sm:flex-row sm:justify-center sm:gap-4">
              <Button asChild size="lg" variant="primary">
                <Link to="/cadastro?tenant=usp">
                  Entrar na lista de espera
                  <ArrowUpRight className="size-4" aria-hidden />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/usp">
                  <BookOpen className="size-4" aria-hidden />
                  Conhecer o FuvestPro
                </Link>
              </Button>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
